import React, { useState } from 'react';
import type { KbaQuestion } from '../../types/api';
import type { AlternativeMethodCallbacks } from '../../types/verification';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import TryAnotherWay from '../ui/TryAnotherWay';

type KbaAnswers = Record<string, string>;

type KbaQuestionsFormProps = AlternativeMethodCallbacks & {
  questions: KbaQuestion[];
  loading: boolean;
  error: string | null;
  onSubmit: (answers: KbaAnswers) => void;
  methodExhausted?: boolean;
};

export default function KbaQuestionsForm({
  questions,
  loading,
  error,
  onSubmit,
  methodExhausted = false,
  onChooseSms,
  onChooseProspect,
}: KbaQuestionsFormProps) {
  const [answers, setAnswers] = useState<KbaAnswers>({});

  const handleOptionSelect = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormComplete()) return;
    onSubmit(answers);
  };

  const isFormComplete = () =>
    questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  return (
    <form
      id="kba-questions-form"
      name="kba-questions-form"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="text-center">
        <Title
          id="kba-questions-title"
          name="kba-questions-title"
          as="h1"
          className="mb-2"
        >
          Security Questions
        </Title>
        <Description
          id="kba-questions-description"
          name="kba-questions-description"
        >
          Please answer the following questions to verify your identity
        </Description>
      </div>

      <div className="space-y-6">
        {questions.map((question, index) => (
          <fieldset
            key={question.id}
            className="rounded-lg border border-gray-200 p-4"
          >
            <legend className="text-sm font-semibold text-gray-700 px-1 mb-3">
              Question {index + 1} of {questions.length}
            </legend>
            <p className="text-base text-gray-800 mb-4">{question.question}</p>
            <div className="space-y-2">
              {question.options.map((option) => {
                const isSelected = answers[question.id] === option.id;
                return (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-[#b3905e] bg-[#b3905e]/5 text-gray-900'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={option.id}
                      checked={isSelected}
                      onChange={() => handleOptionSelect(question.id, option.id)}
                      className="sr-only"
                    />
                    <span
                      className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-[#b3905e] bg-[#b3905e]'
                          : 'border-gray-400'
                      }`}
                    >
                      {isSelected && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <span className="text-sm">{option.text}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <ErrorMessage message={error} />

      <TryAnotherWay
        show={!!error || methodExhausted}
        onChooseSms={onChooseSms}
        onChooseProspect={onChooseProspect}
      />

      {!methodExhausted && (
        <Button
          id="kba-questions-submit"
          name="kba-questions-submit"
          type="submit"
          disabled={loading || !isFormComplete()}
          loading={loading}
          loadingText="Submitting..."
          fullWidth
          size="lg"
        >
          Submit Answers
        </Button>
      )}
    </form>
  );
}
